/**
 * P&L API (TMMAPP-0111; TMMAPI-0147, MM-D-124 / MM-D-127) — the virtual account's P&L by category
 * and its envelope alias, one shape (`VirtualAccountPnl`), wire types from `src/api/generated/`,
 * normalized into the module's domain type. `months` are calendar months (`YYYY-MM`) and every
 * `cells` array aligns with them. Amounts are signed published sums in the virtual account owner's
 * currency: income rows positive, expense rows negative, `net` their sum. `from` / `to` are sent as
 * months; the response echoes the resolved window as dates. The server's default window is the
 * calendar year to date; more than 24 months is `400 VALIDATION_FAILED`. A BUDGET envelope's
 * `budgetCells` are the budget in effect for each month (MM-D-118), `null` before the first.
 */
import { request } from './client';
import type { EnvelopeKind } from './envelopes';
import type {
  VirtualAccountPnl as WirePnl,
  VirtualAccountPnlByEnvelope as WireByEnvelope,
  VirtualAccountPnlCategoryRow as WireCategoryRow,
  VirtualAccountPnlEnvelope as WireEnvelope,
} from './generated';

export interface PnlByEnvelope {
  /** `null` for the virtual account's own Unassigned part. */
  envelopeId: number | null;
  cells: number[];
}

export interface PnlCategoryRow {
  categoryId: number;
  name: string;
  archived: boolean;
  taxReportable: boolean;
  cells: number[];
  totalMinor: number;
  /** Only with `breakdown=envelope`. */
  byEnvelope: PnlByEnvelope[];
}

export interface PnlEnvelope {
  envelopeId: number;
  name: string;
  kind: EnvelopeKind | null;
  archived: boolean;
  openingMinor: number;
  closingMinor: number;
  /** A BUDGET envelope's budget in effect per month; `null` for the other kinds. */
  budgetCells: (number | null)[] | null;
}

export interface PnlUncategorized {
  inCells: number[];
  inTotalMinor: number;
  outCells: number[];
  outTotalMinor: number;
}

export interface Pnl {
  from: string;
  to: string;
  months: string[];
  currency: string | null;
  /** The envelope the report is filtered to; `null` for the whole virtual account. */
  envelopeId: number | null;
  income: PnlCategoryRow[];
  expense: PnlCategoryRow[];
  uncategorized: PnlUncategorized;
  net: { cells: number[]; totalMinor: number };
  openingMinor: number;
  closingMinor: number;
  envelopes: PnlEnvelope[];
  unassigned: { openingMinor: number; closingMinor: number } | null;
}

export interface PnlWindow {
  /** First month, `YYYY-MM`. */
  from?: string | undefined;
  /** Last month, `YYYY-MM`. */
  to?: string | undefined;
}

const ENVELOPE_KINDS: ReadonlySet<string> = new Set(['CASH', 'BUDGET', 'SAVINGS']);

function normalizeByEnvelope(w: WireByEnvelope): PnlByEnvelope | null {
  if (w.unassigned === true) return { envelopeId: null, cells: w.cells };
  if (w.envelopeId == null) return null;
  return { envelopeId: w.envelopeId, cells: w.cells };
}

function normalizeCategoryRow(w: WireCategoryRow): PnlCategoryRow {
  return {
    categoryId: w.categoryId,
    name: w.name,
    archived: w.archived,
    taxReportable: w.taxReportable,
    cells: w.cells,
    totalMinor: w.totalMinor,
    byEnvelope: (w.byEnvelope ?? []).flatMap((row) => {
      const item = normalizeByEnvelope(row);
      return item ? [item] : [];
    }),
  };
}

function normalizeEnvelope(w: WireEnvelope): PnlEnvelope {
  const kind = ENVELOPE_KINDS.has(w.kind) ? w.kind : null;
  return {
    envelopeId: w.envelopeId,
    name: w.name,
    kind,
    archived: w.archived,
    openingMinor: w.openingMinor,
    closingMinor: w.closingMinor,
    budgetCells: kind === 'BUDGET' && w.budget != null ? w.budget.cells : null,
  };
}

/** A P&L. Optional fields (`currency`, `envelopeId`, `unassigned`) are `null` when absent. */
export function normalizePnl(w: WirePnl): Pnl {
  return {
    from: w.from,
    to: w.to,
    months: w.months,
    currency: w.currency ?? null,
    envelopeId: w.envelopeId ?? null,
    income: w.income.map(normalizeCategoryRow),
    expense: w.expense.map(normalizeCategoryRow),
    uncategorized: {
      inCells: w.uncategorized.inCells,
      inTotalMinor: w.uncategorized.inTotalMinor,
      outCells: w.uncategorized.outCells,
      outTotalMinor: w.uncategorized.outTotalMinor,
    },
    net: { cells: w.net.cells, totalMinor: w.net.totalMinor },
    openingMinor: w.openingMinor,
    closingMinor: w.closingMinor,
    envelopes: w.envelopes.map(normalizeEnvelope),
    unassigned:
      w.unassigned == null
        ? null
        : { openingMinor: w.unassigned.openingMinor, closingMinor: w.unassigned.closingMinor },
  };
}

/** A civil date or a month → its month (`YYYY-MM`); the routes take months. */
export function toMonth(value: string | undefined): string | undefined {
  return value === undefined ? undefined : value.slice(0, 7);
}

export const pnlApi = {
  /**
   * GET /v1/envelopes/{envelopeId}/pnl — the virtual account P&L filtered to the envelope (the
   * same shape). `from` after `to`, or more than 24 months, is the server's `VALIDATION_FAILED`.
   */
  async envelope(envelopeId: number, window: PnlWindow = {}): Promise<Pnl> {
    return normalizePnl(
      await request<WirePnl>('GET', `/v1/envelopes/${envelopeId}/pnl`, {
        query: { from: window.from, to: window.to },
      }),
    );
  },
  /**
   * GET /v1/virtual-accounts/{id}/pnl — by category; `breakdown: 'envelope'` splits each category
   * row by envelope.
   */
  async virtualAccount(
    virtualAccountId: number,
    window: PnlWindow & { breakdown?: 'envelope'; envelopeId?: number } = {},
  ): Promise<Pnl> {
    return normalizePnl(
      await request<WirePnl>('GET', `/v1/virtual-accounts/${virtualAccountId}/pnl`, {
        query: {
          from: window.from,
          to: window.to,
          breakdown: window.breakdown,
          envelopeId: window.envelopeId,
        },
      }),
    );
  },
};
